import React, { useContext } from 'react';
import { ActionOptions, BindProperties, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_RULE_SEARCHES, EFFECTIVE, NONE } from '../../utils/Constants';
import PermissionsChecker, { CATEGORIES } from '../../utils/PermissionsChecker';
import { ColumnProps } from 'antd/lib/table';
import '../../views/ShoppingDiagnostics/SDResult/SDPostSearch/SDPostSearchTable/SDPostSearchTable.css';
import TableHeader from './TableHeader';
import styles from './TableLayoutV2.module.scss';
import Tooltip from 'antd/lib/tooltip';
import TableFormatter from '../../utils/TableFormatter';
import { IActionOptions, ISelectedRowsObject } from './FiltersTableLayoutsWrapperV2';
import ResizableAndDraggableTable, { ResizableTitle } from '../../components/Table/ResizableAndDraggableTable/ResizableAndGraggableTable';
import { AuthContext } from '../../auth/AuthContext';

interface ITableLayoutV2Props {
  id: string; // to be add enum
  history?: any;
  records?: Array<any>; // to be typed
  totalItems?: number;
  currentPage: number;
  actionOptions?: IActionOptions;
  addButtonName?: string;
  columns: Array<ColumnProps<any>>;
  category?: CATEGORIES;
  path: string;
  handlePageChange: (pageNumber: number) => void;
  selectedRowsObject: ISelectedRowsObject;
  handleRowSelect: (selectedRowKeys: any[], selectedRows: any[]) => void;
  handleActionOptionClick: (actionOption: ActionOptions) => void;
  handleAddRuleButtonClick: () => any;
  handleSortClick: (sortValue: string) => void;
  ifSubrule?: boolean;
  disabledButton?: boolean;
  selectCurrentPage?: boolean;
  selectionEnabled?: boolean;
  checkboxActions: (id: string) => void;
  checkcheckbox: (id: string) => any;
  supplierSelected?: boolean;
  filtersBrandAndMarketSelected?: boolean;
  setShowSubRuleEditor?: (val: boolean) => void;
  seteditSubRule?: (val: boolean) => void;
  setviewSubRule?: (val: boolean) => void;
  setcopySubRule?: (val: boolean) => void;
  showHistoryModal?: boolean;
  setShowHistoryModal?: (val: boolean) => void;
  onExport?: () => void;
}

interface IAdditionalColumnSettings {
  isMarkWhenDeletedNeeded: boolean;
  isMarkWhenDisabledNeeded: boolean;
  isMarkWhenNoneNeeded: boolean;
  transformValueToTitle?: (value: any) => any;
}

interface ISetOfColumnsWithAdditionalSettings {
  description?: IAdditionalColumnSettings;
  comment?: IAdditionalColumnSettings;
  brandAndMarket?: IAdditionalColumnSettings;
  marketingCarriers: IAdditionalColumnSettings;
  carriers: IAdditionalColumnSettings;
  osIs: IAdditionalColumnSettings;
  fareTypes: IAdditionalColumnSettings;
  distributionChannels?: IAdditionalColumnSettings;
  suppliers?: IAdditionalColumnSettings;
  relevance?: IAdditionalColumnSettings;
  state?: IAdditionalColumnSettings;
  supplier?: IAdditionalColumnSettings;
  preferredCarriers: IAdditionalColumnSettings;
  permittedCarriers: IAdditionalColumnSettings;
  prohibitedCarriers: IAdditionalColumnSettings;
  disabled: IAdditionalColumnSettings;
  maxConnectionTime: IAdditionalColumnSettings;
  contactEmail: IAdditionalColumnSettings;
  contactMobile: IAdditionalColumnSettings;
  overrideDays?: IAdditionalColumnSettings;
  offsetDays?: IAdditionalColumnSettings;
}

const TableLayoutV2: React.FC<ITableLayoutV2Props> = (props) => {
const {
  id,
  records,
  totalItems,
  currentPage,
  columns,
  category,
  path,
  history,
  handleSortClick,
  handlePageChange,
  actionOptions,
  addButtonName,
  handleActionOptionClick,
  selectedRowsObject,
  handleRowSelect,
  handleAddRuleButtonClick,
  disabledButton,
  ifSubrule,
  selectionEnabled,
  checkboxActions,
  checkcheckbox,
  supplierSelected,
  filtersBrandAndMarketSelected,
  setShowSubRuleEditor,
  seteditSubRule,
  setviewSubRule,
  setcopySubRule,
  setShowHistoryModal,
  showHistoryModal,
  onExport,
} = props;

  const paginationSettings = {
    current: currentPage,
    pageSize: ifSubrule ? DEFAULT_PAGE_SIZE_RULE_SEARCHES : DEFAULT_PAGE_SIZE,
    showSizeChanger: false,
    onChange: (page: number) => handlePageChange(page),
    total: totalItems,
  };

  const authContext: any = useContext(AuthContext);
  const { userPermissions } = authContext;

  const setCellStyles = (isMarkedWhenDeleted: boolean = false, isMarkedWhenDisabled: boolean = false, isMarkedWhenNone: boolean = false) => {
    let additionalStyles = '';
    if (isMarkedWhenDeleted) additionalStyles += 'markedWhenDeleted ';
    if (isMarkedWhenDisabled) additionalStyles += 'markedWhenDisabled ';
    if (isMarkedWhenNone) additionalStyles += 'markedWhenNone ';
    return additionalStyles;
  };

  const additionalColumnsSettings: ISetOfColumnsWithAdditionalSettings = {
    description: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
    },
    comment: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
    },
    brandAndMarket: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: TableFormatter.formatBrandAndMarketObject,
    },
    marketingCarriers: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        const items = TableFormatter.formatSimpleObjectsArray(value, '', 'code');
        return (
          <Tooltip placement="topLeft" title={items}>
            {items}
          </Tooltip>
        );
      },
    },
    carriers: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        return TableFormatter.formatSecondaryValuesArrayIfFalse({
          items: value,
          isSecondaryFieldName: EFFECTIVE,
          labelFieldName: 'code',
          cursive: false,
        });
      },
    },
    osIs: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        const items = TableFormatter.formatStringsArray(value);
        return (
          <Tooltip placement="topLeft" title={items}>
            {items}
          </Tooltip>
        );
      },
    },
    fareTypes: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        const items = TableFormatter.formatSimpleObjectsArray(value);
        return (
          <Tooltip placement="topLeft" title={items}>
            {items}
          </Tooltip>
        );
      },
    },
    distributionChannels: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        return TableFormatter.formatSecondaryValuesArrayIfFalse({
          items: value,
          isSecondaryFieldName: EFFECTIVE,
          labelFieldName: 'label',
          cursive: false,
        });
      },
    },
    suppliers: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        return TableFormatter.formatSecondaryValuesArrayIfFalse({
          items: value,
          isSecondaryFieldName: EFFECTIVE,
          labelFieldName: 'label',
          cursive: false,
        });
      },
    },
    supplier: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        return value.label;
      },
    },
    preferredCarriers: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,

      transformValueToTitle: (value: any) => {
        return TableFormatter.formatSecondaryValuesArrayIfFalse({
          items: value,
          isSecondaryFieldName: EFFECTIVE,
          labelFieldName: 'code',
          cursive: false,
        });
      },
    },
    permittedCarriers: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        return TableFormatter.formatSecondaryValuesArrayIfFalse({
          items: value,
          isSecondaryFieldName: EFFECTIVE,
          labelFieldName: 'code',
          cursive: false,
        });
      },
    },
    prohibitedCarriers: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value: any) => {
        return TableFormatter.formatSecondaryValuesArrayIfFalse({
          items: value,
          isSecondaryFieldName: EFFECTIVE,
          labelFieldName: 'code',
          cursive: false,
        });
      },
    },
    relevance: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
    },
    state: {
      isMarkWhenDeletedNeeded: false,
      isMarkWhenDisabledNeeded: false,
      isMarkWhenNoneNeeded: false,
      transformValueToTitle: (value) => {
        return String(value);
      },
    },
    disabled: {
      isMarkWhenDeletedNeeded: false,
      isMarkWhenDisabledNeeded: false,
      isMarkWhenNoneNeeded: false,
      transformValueToTitle: (value) => {
        return String(value);
      },
    },
    maxConnectionTime: {
      isMarkWhenDeletedNeeded: false,
      isMarkWhenDisabledNeeded: false,
      isMarkWhenNoneNeeded: false,
      transformValueToTitle: (value) => {
        let newValue = value?.split(':');
        newValue?.splice(2, 1);
        return newValue?.join(':');
      },
    },
    contactEmail: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value) => {
        return String(value || '');
      },
    },
    contactMobile: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: true,
      transformValueToTitle: (value) => {
        return String(value || '');
      },
    },
    overrideDays: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: false,
      // explicit null/undefined check avoids treating 0 as none
      transformValueToTitle: (value: any) => value === null || value === undefined ? NONE : value,
    },
    offsetDays: {
      isMarkWhenDeletedNeeded: true,
      isMarkWhenDisabledNeeded: true,
      isMarkWhenNoneNeeded: false,
    },
  };

  const renderCellsForColumn = (columnSettings: IAdditionalColumnSettings) => {
    const { isMarkWhenDeletedNeeded, isMarkWhenDisabledNeeded, isMarkWhenNoneNeeded, transformValueToTitle } = columnSettings || {};
    return function (val: any, record: any) {
      let value = val;
      if (isMarkWhenNoneNeeded && !value) value = NONE;
      return (
        <Tooltip placement="topLeft" title={transformValueToTitle ? transformValueToTitle(value) : value}>
          <span
            className={setCellStyles(
              isMarkWhenDeletedNeeded ? record.state?.some((s: any) => s === 'Deleted') : false,
              isMarkWhenDisabledNeeded ? record.state?.some((s: any) => s === 'Disabled') : false,
              isMarkWhenNoneNeeded ? value === NONE : false
            )}
          >
            {transformValueToTitle ? transformValueToTitle(value) : value}
          </span>
        </Tooltip>
      );
    };
  };

  const prepareColumns = (cols: Array<ColumnProps<any>>) => {
    let columns = [...cols];
    columns?.forEach((col, index) => {
      let column = { ...col };
      column.key = String(column.dataIndex);
      column.sorter = column?.sorter ?? true;
      // column.ellipsis = (column.ellipsis === undefined || column.ellipsis === true)
      if (!column.render) column.render = renderCellsForColumn((additionalColumnsSettings as any)[column.key]);
      if (!column.onCell && column.key === BindProperties.Supplier && ifSubrule) {
        column.onCell = (record: any, rowIndex: any) => {
          return {
            onClick: () => handleSupplierLink(record),
          };
        };
      }
      columns[index] = column;
    });
    return columns;
  };

  const prepareRecords = (records?: Array<any>) => {
    return records?.map((record: any) => {
      return { ...record, key: record.id };
    });
  };

  const handleGoToEditLink = (record: any) => {
    if (record.allowEdit) {
      return history.push({ pathname: `/${path}/${record.id}` });
    } else {
      return history.push({ pathname: `/${path}/${record.id}`, viewDisabled: false, actionOption: ActionOptions.View });
    }
  };

  const getActionOptions = () => {
    if (props?.history?.location?.actionOption === ActionOptions.View) {
      return actionOptions?.whenReadOnly;
    }
    const allowEditCheck = selectedRowsObject.selectedRows.filter((record: any) => record.allowEdit === false);
    return PermissionsChecker.isReadOnly(category, userPermissions) || allowEditCheck.length > 0 ? actionOptions?.whenReadOnly : actionOptions?.whenFullControl;
  };

  const handleSupplierLink = (value: any) => {
    handleRowSelect([value.id], [value]);
    setShowSubRuleEditor!(true);
    seteditSubRule!(true);
    setcopySubRule!(false);  // ✅ Reset copySubRule flag when opening via supplier link
    if (getActionOptions() === actionOptions?.whenReadOnly) {
      setviewSubRule!(true);
    } else {
      setviewSubRule!(false);
    }
  };

  const rowSelection = {
    hideSelectAll: true,
    selectedRowKeys: selectedRowsObject.selectedRowKeys,
    onChange: handleRowSelect,
  };

  return (
    <>
      <TableHeader
        totalItems={totalItems}
        allActionOptions={actionOptions?.whenFullControl}
        actionOptionsToRender={getActionOptions()}
        handleActionOptionClick={handleActionOptionClick}
        optionsAreActive={Boolean(selectedRowsObject.selectedRows.length)}
        itemsSelected={selectedRowsObject.selectedRows.length}
        addButtonName={addButtonName}
        disabledButton={disabledButton}
        handleAddRuleButtonClick={handleAddRuleButtonClick}
        checkboxActions={checkboxActions}
        checkcheckbox={checkcheckbox}
        supplierSelected={supplierSelected}
        filtersBrandAndMarketSelected={filtersBrandAndMarketSelected}
        ifSubrule={ifSubrule}
        handleRowSelect={handleRowSelect}
        selectedRowsObject={selectedRowsObject}
        setShowHistoryModal={setShowHistoryModal}
        showHistoryModal={showHistoryModal}
        onExport={onExport}
      />
      <ResizableAndDraggableTable
        className={styles.customTableBody}
        id={id}
        columns={prepareColumns(columns)}
        dataSource={prepareRecords(records)}
        pagination={paginationSettings}
        rowSelection={!ifSubrule || selectionEnabled ? rowSelection : null}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        onRow={
          !ifSubrule
            ? (record: any) => {
                return {
                  onClick: () => handleGoToEditLink(record),
                };
              }
            : null
        }
        handleSortClick={handleSortClick}
      />
    </>
  );
};

export default TableLayoutV2;
